import { useQuery } from "@apollo/client";
import { Spin, Tabs } from "antd";
import { message } from "utils/ciq-message";
import {
  ProjectContext,
  isPermissionNotGrantted
} from "context/ProjectProvider";
import { useProjectPK } from "hooks/project";
import ProjectUsersList from "pages/project-users";
import { useContext, useEffect, useRef, useState } from "react";
import { getLoggedinUserId } from "services/auth";
import { updateProjectDetails } from "services/common";
import {
  QUERY_GET_VENDOR_ID,
  QUERY_PROJECT_USERS
} from "services/graphQL/queries";
import { useHistory, useParams } from "react-router";
import ProjectCompanies from "pages/project-companies";
import ProjectCalendar from "pages/project-calendar";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { EUserTypes, ProjectPermissionEnum } from "../../../constants";
import ProjectDetailEdit from "./edit-project-detail";
import DistribuionGroupsSettings from "../module-settings/submittal-settings/components/distribuion-groups";
import PermissionsMatrix from "components/permissions-matrix";

function GeneralSettings({ isInternalGCser }: { isInternalGCser: boolean }) {
  const history = useHistory();
  const { projectId, tab2Id, tab3Id } = useParams() as any;
  const [tabIndex, setTabIndex] = useState<string>(tab2Id || "project-users");
  const { gqlClientForProject, tokenContents, eventLogs, tokenRetrievalState } =
    useContext(ProjectContext);

  const { data: projectPkData, refetch: refetchProjectPkData } = useProjectPK(
    projectId,
    gqlClientForProject
  );
  const { data: vendorData } = useQuery(QUERY_GET_VENDOR_ID, {
    variables: {
      userId: getLoggedinUserId()
    },
    client: gqlClientForProject,
    skip:
      !gqlClientForProject ||
      tokenContents?.userType === EUserTypes[EUserTypes.GENERAL_CONTRACTOR]
  });
  const projectUserVariables: any = {
    where: { project_id: { _eq: projectId } }
  };
  const { data: usersData, refetch: refetchUsersData } = useCIQQuery(
    QUERY_PROJECT_USERS,
    {
      client: gqlClientForProject,
      variables: projectUserVariables,
      skip:
        !tokenContents ||
        !gqlClientForProject ||
        (tokenContents?.userType !==
          EUserTypes[EUserTypes.GENERAL_CONTRACTOR] &&
          !vendorData)
    }
  );

  const updateProjectData = async (projectValues: any): Promise<void> => {
    const updateResponse = await updateProjectDetails(
      projectId,
      projectValues,
      tokenRetrievalState?.token
    );

    if (!updateResponse?.success) {
      message.error(
        updateResponse?.errorMsg ||
          updateResponse?.data?.message ||
          "Failed to update project details"
      );
      return;
    }

    message.success(
      updateResponse?.data?.message || "Project details updated successfully"
    );
    refetchProjectPkData();
  };

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (
        eventLogs.some(
          (log) =>
            log.data_source === "project_users" &&
            (log.change_type === "update" || log.change_type === "create")
        )
      ) {
        refetchUsersData();
      }

      if (
        eventLogs.some(
          (log) => log.data_source === "project" && log.change_type === "update"
        )
      ) {
        refetchProjectPkData();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetchUsersData, refetchProjectPkData]);

  const tabItems = [
    {
      label: "Project Users",
      key: "project-users",
      children:
        tab3Id === "permissions" ? (
          <PermissionsMatrix
            onBack={() =>
              history.replace(
                `/project/${projectId}/settings/general/project-users`
              )
            }
          />
        ) : (
          <ProjectUsersList
            internalUsers={{ project_users: usersData?.project_users ?? [] }}
          />
        )
    },
    {
      label: "Project Companies",
      key: "project-companies",
      children: <ProjectCompanies />
    },
    {
      label: "Details",
      key: "details",
      children:
        projectPkData && projectPkData.project_by_pk ? (
          <ProjectDetailEdit
            projectInfo={projectPkData.project_by_pk}
            updateProjectData={updateProjectData}
            hasEditPermission={
              !isPermissionNotGrantted(
                ProjectPermissionEnum.EditProjectDetail,
                tokenContents?.role ?? ""
              )
            }
          />
        ) : (
          <div className="flex justify-center items-center w-full h-full min-h-[500px]">
            <Spin />
          </div>
        )
    },
    {
      label: "Calendar",
      key: "calendar",
      children: <ProjectCalendar />
    },
    {
      label: "Distribution Groups",
      key: "distribution-groups",
      children: <DistribuionGroupsSettings />
    }
  ];

  const secondTabItems = [
    {
      label: "Project Users",
      key: "project-users",
      children:
        tab3Id === "permissions" ? (
          <PermissionsMatrix
            onBack={() =>
              history.replace(
                `/project/${projectId}/settings/general/project-users`
              )
            }
          />
        ) : (
          <ProjectUsersList
            internalUsers={{ project_users: usersData?.project_users ?? [] }}
          />
        )
    }
  ];

  return (
    <div className="w-full pt-1 module-settings--parentTabs">
      <Tabs
        onChange={(activeKey) => {
          history.push(
            `/project/${projectId}/settings/general/${activeKey.toString()}`
          );
          setTabIndex(activeKey);
        }}
        activeKey={tabIndex}
        items={isInternalGCser ? tabItems : secondTabItems}
        data-testid="index-tabs-project-general-settings"
      />
    </div>
  );
}
export default GeneralSettings;
